/**
 * form — the English half, typed against the German module, so a
 * missing or misspelled key is a type error in this file.
 */
import type { form as de_form } from '../de/form';

export const form: Record<keyof typeof de_form, string> = {
  'print.note': 'Please use the “Download PDF” button – this page is not meant to be printed directly.',
  'form.line': 'Line {n}',

  /* ---------------- sections ---------------- */
  'sec.flug.title': 'Flight',
  'sec.flug.nav': 'Flight',
  'sec.route.title': 'Route & checkpoints',
  'sec.route.nav': 'Route',
  'sec.zeit.title': 'Times',
  'sec.zeit.nav': 'Times',
  'sec.wx.title': 'Weather briefing',
  'sec.wx.nav': 'Weather',
  'sec.atis.title': 'ATIS / aerodrome weather',
  'sec.atis.nav': 'ATIS',
  'sec.b1.title': 'Clearances / weather / info',
  'sec.b1.nav': 'Clearances',
  'sec.v5.title': 'ATS briefing',
  'sec.v5.nav': 'ATS',
  'sec.fq.title': 'Frequencies',
  'sec.fq.nav': 'Frequencies',
  'sec.aip.title': 'Aerodrome data from AIP',
  'sec.aip.nav': 'AIP',
  'sec.fuel.title': 'Fuel calculation',
  'sec.fuel.nav': 'Fuel',
  'sec.mb.title': 'Mass & balance',
  'sec.mb.nav': 'Mass',
  'sec.tl.title': 'Take-off & landing distances',
  'sec.tl.nav': 'Take-off/landing',

  /* ---------------- field labels (aria/tooltip) ---------------- */
  'lbl.datum': 'Date',
  'lbl.von': 'Departure aerodrome (von)',
  'lbl.nach': 'Destination aerodrome (nach)',
  'lbl.lfz_muster': 'Aircraft type',
  'lbl.lfz_kennz': 'Aircraft registration',
  'lbl.etd': 'ETD – estimated time of departure (UTC)',
  'lbl.atd': 'ATD – actual time of departure',
  'lbl.eta': 'ETA – estimated time of arrival',
  'lbl.ata': 'ATA – actual time of arrival',
  'lbl.ss': 'SS – sunset (UTC)',
  'lbl.safe_time': 'Safe flight time (max. endurance − 30 min)',
  'lbl.total_dist': 'Total distance (P14)',
  'lbl.total_time': 'Total flight time (P18)',
  'lbl.gafor': 'GAFOR areas/values',
  'lbl.gafor_check': 'GAFOR briefing obtained',
  'lbl.gafor_bis': 'Briefing valid until (UTC)',
  'lbl.fuel_vorrat_zeit': 'Fuel on board: max. endurance',
  'lbl.fuel_safe_time': 'Safe flight time',
  'lbl.alt_name': 'Alternate aerodrome',

  /* label patterns */
  'lbl.legRow': 'Leg {n}: {what}',
  'lbl.altRow': 'Alternate leg: {what}',
  'lbl.freigabenRow': 'Clearances/weather/info, line {n}',
  'lbl.fqRow': 'Frequency {where}: {what}',
  'lbl.atisRow': 'ATIS line {n}: {what}',
  'lbl.cpRow': '{what} (line {n})',
  'lbl.fsRow': 'ATS briefing, line {n}',
  'lbl.wxRow': 'Weather briefing, line {n}',
  'lbl.aipRow': 'AIP {where}: {what}',
  'lbl.fuelRow': 'Fuel {row}: {what}',
  'lbl.verbrauchRow': 'Consumption data, line {n}',
  'lbl.mbUnit': '{what} unit',
  'lbl.mbRow': '{row}: {what}',
  'lbl.tlRow': '{row} – {col}',

  /* frequencies */
  'fq.dep': 'departure aerodrome',
  'fq.dest': 'destination aerodrome',
  'fq.ground': 'Ground',
  'fq.twr': 'Tower',
  'fq.info': 'INFO',

  /* AIP */
  'aip.p1': 'departure aerodrome',
  'aip.p2f': 'destination aerodrome',
  'aip.p20': 'alternate aerodrome',
  'aip.rwy': 'Runway direction',
  'aip.len': 'Runway length (m)',
  'aip.dist15': 'Distance 15 m',
  'aip.elev': 'Elevation (ft)',
  'aip.temp': 'Temperature',
  'aip.wind': 'Wind',
  'aip.slope': 'Slope',
  'aip.belag': 'Surface',
  'aip.gras': 'Grass runway',
  'aip.surface': 'Surface condition',
  'aip.restr': 'Restrictions',

  /* fuel */
  'fuel.reise': 'cruise',
  'fuel.anlassen': 'start-up/taxi',
  'fuel.steig': 'climb',
  'fuel.anab': 'arrival and departure',
  'fuel.altn': 'alternate aerodrome',
  'fuel.reserve': 'reserve',
  'fuel.mindest': 'minimum fuel required',
  'fuel.extra': 'extra fuel',
  'fuel.vorrat': 'fuel on board',
  'fuel.zeit': 'Flight time',
  'fuel.liter': 'Litres',

  /* mass & balance */
  'mb.masse': 'Mass',
  'mb.moment': 'Moment',
  'mb.leer': 'Empty mass',
  'mb.kna': 'Fuel, unusable',
  'mb.schmier': 'Lubricants',
  'mb.grund': 'Basic empty mass',
  'mb.sitz1': 'Seat row 1',
  'mb.sitz2': 'Seat row 2',
  'mb.sitz3': 'Seat row 3',
  'mb.gep_vorn': 'Baggage front',
  'mb.gep_mitte': 'Baggage centre',
  'mb.gep_hinten': 'Baggage rear',
  'mb.kausf': 'Fuel, usable',
  'mb.start': 'Take-off mass',

  /* take-off & landing distances */
  'tl.bahn': 'Runway length available',
  'tl.verf': 'Distance available (15 m)',
  'tl.grund': 'Basic distance (AFM)',
  'tl.hoehe': 'Elevation allowance',
  'tl.temp': 'Temperature allowance',
  'tl.wind': 'Wind effect',
  'tl.neig': 'Slope allowance',
  'tl.gras': 'Grass runway allowance',
  'tl.oberfl': 'Surface allowance',
  'tl.benoetigt': 'Distance required',
  'tl.sr': 'Take-off: ground roll',
  'tl.ss': 'Take-off distance',
  'tl.ll': 'Landing distance',
  'tl.lr': 'Landing: ground roll',

  /* ---------------- form captions ---------------- */
  'form.view': 'Form view',
  'form.sections': 'Sections',
  'form.dep': 'Departure',
  'form.dest': 'Destination',
  'form.alt': 'Alternate',
  'form.total': 'Total',
  'form.leg': 'Leg {n}',
  'form.datum': 'Date',
  'form.date.ph': 'DD.MM.YYYY',
  'form.von': 'From (departure)',
  'form.nach': 'To (destination)',
  'form.lfz_kennz.ph': 'D-….',
  'form.aircraft': 'Aircraft',
  'form.aircraft.aria': 'Aircraft this plan is flown with',
  'form.aircraft.empty': 'none',
  'form.aircraft.manage': 'Manage aircraft →',
  'form.aircraft.none': 'Add an aircraft →',

  'form.route.note': 'Wind as DDD/SS, VAR with a sign. WCA, mwSK, mwK, GS and the times follow from them.',
  'card.name': 'Next checkpoint',
  'card.dep': 'Departure aerodrome',
  'card.rest_dist': 'Rem. NM',
  'card.rest_time': 'Rem. min',
  'card.total_dist': 'Total NM',
  'card.total_time': 'Total min',

  'form.etd': 'ETD (UTC)',
  'form.atd': 'ATD',
  'form.eta': 'ETA',
  'form.ata': 'ATA',
  'form.safe_time': 'Safe flight time',

  'form.gafor_check': 'GAFOR briefing',
  'form.gafor_bis': 'valid until (UTC)',
  'form.gafor': 'GAFOR areas/codes',
  'form.wx.note':
    'Does not replace an official weather briefing.',
  'form.wx.lines': 'Weather briefing (lines of the form):',

  'form.atis.station': 'Station/frequency',
  'form.atis.code': 'Code',
  'form.atis.rwy': 'RWY',
  'form.atis.tl': 'TL',
  'form.atis.zeit': 'Time (UTC)',
  'form.atis.wind': 'Wind',
  'form.atis.sicht': 'Vis.',
  'form.atis.wolken': 'Clouds',
  'form.atis.temp': 'Temp./DP',
  'form.atis.qnh': 'QNH',
  'form.atis.tendenz': 'Trend',

  'form.aip.rwy': 'RWY dir.',
  'form.aip.len': 'RWY length (m)',
  'form.aip.dist15': 'Distance 15 m',
  'form.aip.elev': 'Elevation (ft)',
  'form.aip.temp': 'Temp. (°C)',
  'form.aip.wind': 'Wind (° kt)',
  'form.aip.slope': 'Slope',
  'form.aip.belag': 'Surface',
  'form.aip.gras': 'Grass',
  'form.aip.surface': 'Surface condition',
  'form.aip.restr': 'Restrictions',
  'belag.asph': 'ASPH – asphalt',
  'belag.beton': 'Concrete',
  'belag.gras': 'Grass',
  'belag.schotter': 'Gravel',
  'belag.sand': 'Sand',

  'form.fuel.reise': 'Cruise (departure–destination)',
  'form.fuel.anlassen': 'Allowance: start-up, taxi',
  'form.fuel.steig': 'Allowance: climb',
  'form.fuel.anab': 'Arrival and departure (min. 10 min)',
  'form.fuel.altn': 'Alternate aerodrome',
  'form.fuel.reserve': 'Reserve (min. 30 min)',
  'form.fuel.mindest': 'Minimum fuel required',
  'form.fuel.extra': 'Extra fuel',
  'form.fuel.vorrat': 'Fuel on board (max. endurance)',
  'form.fuel.safe': 'Safe flight time (= max. − 30 min)',
  'form.fuel.verbrauch': 'Consumption data',

  'form.mb.units': 'Units',
  'form.mb.unit_masse': 'Unit of mass',
  'form.mb.unit_moment': 'Unit of moment',
  'form.tl.note': 'Distance required = basic distance + all allowances.',

  /* ---------------- shared view ---------------- */
  'shared.readonly': 'shared view (read-only)',
  'shared.own': 'Create your own plan',
  'shared.invalid': 'The link is invalid or has expired.',

  'form.pane.note': 'The form, exactly as it prints.',
  'shared.madeWith': 'Made with Aether',
};
